import React, { useEffect, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { LayoutDashboard, FileText, BedDouble, Settings, LogOut, PieChart, Menu, X, Calendar, BadgeTurkishLira, PartyPopper } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { jsonAuthHeaders } from '../utils/api';

const API_URL = process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:5000/api';

export default function Sidebar() {
  const { logout, currentUser } = useAuth();
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  const [logos, setLogos] = useState({ light: '', dark: '' });
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await fetch(`${API_URL}/settings/logos`);
        const data = await response.json();
        setLogos(data);
        
        // Apply global settings
        if (data.pageTitle) document.title = data.pageTitle;
        if (data.favicon) {
          let link = document.querySelector("link[rel~='icon']");
          if (!link) {
              link = document.createElement('link');
              link.rel = 'icon';
              document.getElementsByTagName('head')[0].appendChild(link);
          }
          link.href = data.favicon;
        }
      } catch (e) {
        console.error("Logo ayarları yüklenemedi", e);
      }
    };
    fetchSettings();
  }, []);

  // Önce oturum kapatılır, çıkış logu sonra (token'la) gönderilir: token süresi dolmuşsa log 401 alır ama
  // oturum zaten kapalı olduğu için AuthContext "oturumunuzun süresi doldu" uyarısı göstermez.
  const handleLogout = async () => {
    const user = currentUser;
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error("Çıkış hatası:", error);
    }
    if (user?.token) {
      fetch(`${API_URL}/activityLogs`, {
        method: 'POST',
        headers: jsonAuthHeaders(user.token),
        body: JSON.stringify({
          action: 'Çıkış Yapıldı',
          description: 'Kullanıcı sistemden çıkış yaptı.',
          performedBy: { uid: user.uid, email: user.email }
        })
      }).catch(() => {});
    }
  };

  const menuItems = [
    { path: '/', name: 'Genel Bakış', icon: <LayoutDashboard size={20} /> },
    { path: '/offers', name: 'Teklifler', icon: <FileText size={20} /> },
    { path: '/events', name: 'Organizasyonlar', icon: <PartyPopper size={20} /> },
    { path: '/calendar', name: 'Takvim', icon: <Calendar size={20} /> },
    { path: '/rooms', name: 'Oda Yönetimi', icon: <BedDouble size={20} /> },
    { path: '/rates', name: 'Fiyat Takvimi', icon: <BadgeTurkishLira size={20} /> },
    // Rapor görüntüleme yetkisi kontrolü
    ...(currentUser?.permissions?.view_reports ? [
      { path: '/reports', name: 'Raporlar', icon: <PieChart size={20} /> }
    ] : []),
    // Sadece yazma yetkisi olanlar Ayarlar'ı görebilir
    ...(currentUser?.permissions?.write ? [
      { path: '/settings', name: 'Ayarlar', icon: <Settings size={20} /> }
    ] : []),
  ];

  return (
    <>
      {/* Mobil Header (Sabit Üst Bar) */}
      <div className="md:hidden fixed top-0 left-0 right-0 h-16 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 z-30 flex items-center justify-between px-4 shadow-sm">
        <div className="flex items-center gap-3">
            <button 
                onClick={() => setIsOpen(true)}
                className="p-2 text-gray-600 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            >
                <Menu size={24} />
            </button>
            {darkMode && logos.dark ? (
              <img src={logos.dark} alt="Logo" className="h-8 object-contain" />
            ) : !darkMode && logos.light ? (
              <img src={logos.light} alt="Logo" className="h-8 object-contain" />
            ) : (
              <span className="font-bold text-lg text-gray-800 dark:text-white">Ramada Panel</span>
            )}
        </div>
      </div>

      {/* Mobil Overlay (Arka planı karartma) */}
      {isOpen && (
        <div 
          className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-40 backdrop-blur-sm transition-opacity"
          onClick={() => setIsOpen(false)}
        />
      )}

      <div className={`h-screen w-64 bg-white dark:bg-gray-900 text-gray-800 dark:text-white flex flex-col fixed left-0 top-0 z-50 shadow-xl transition-transform duration-300 border-r border-gray-200 dark:border-gray-800 print:hidden ${isOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0`}>
      <div className="p-6 border-b border-gray-100 dark:border-gray-800 flex justify-between items-center h-24">
        {darkMode && logos.dark ? (
          <img src={logos.dark} alt="Logo" className="max-h-16 object-contain" />
        ) : !darkMode && logos.light ? (
          <img src={logos.light} alt="Logo" className="max-h-16 object-contain" />
        ) : (
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white tracking-wide">
            Ramada<span className="text-red-600 text-sm block font-normal">Rezervasyon Paneli</span>
          </h1>
        )}
        {/* Mobilde Sidebar içindeki kapatma butonu */}
        <button onClick={() => setIsOpen(false)} className="md:hidden text-gray-500 hover:text-gray-700">
            <X size={24} />
        </button>
      </div>
      
      <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
        {menuItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            onClick={() => setIsOpen(false)}
            className={({ isActive }) =>
              `flex items-center space-x-3 px-4 py-3 rounded-xl transition-all duration-200 group ${
                isActive 
                  ? 'bg-gradient-to-r from-red-600 to-red-500 text-white shadow-lg shadow-red-200 dark:shadow-none' 
                  : 'text-gray-600 dark:text-gray-400 hover:bg-red-50 dark:hover:bg-gray-800 hover:text-red-600 dark:hover:text-red-400'
              }`
            }
          >
            {item.icon}
            <span>{item.name}</span>
          </NavLink>
        ))}
      </nav>

      <div className="p-4 border-t border-gray-100 dark:border-gray-800">
        <button
          onClick={handleLogout}
          className="flex items-center space-x-3 px-4 py-3 w-full text-left text-gray-600 dark:text-gray-400 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 dark:hover:text-red-400 rounded-xl transition-colors"
        >
          <LogOut size={20} />
          <span>Çıkış Yap</span>
        </button>
        <div className="mt-2 pt-4 text-center border-t border-gray-100 dark:border-gray-800">
            <p className="text-xs text-gray-400 font-medium opacity-70 leading-tight">
                by AMC Fikir Sanat<br/>Rezervasyon Takip
            </p>
        </div>
      </div>
    </div>
    </>
  );
}